import { useMemo } from 'react'

import { useFloorPlan } from '../hooks/useFloorPlan'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'

import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons'
import { Box, IconButton } from '@chakra-ui/react'

import { useAppSelector } from '@/redux/hooks'
import { selectFacilityDrawerOpen } from '@/redux/ui/uiSlice'

import { CheckPointsPanel, DarkCheckPointsPanel } from '../checkpoints-panel'
import { DarkDevicesPanel, DevicesPanel } from '../devices-panel'
import { DarkFloorsPanel, FloorsPanel } from '../floors-panel'
import { DarkTourPlansPanel, TourPlansPanel } from '../tour-plans-panel'
import { FloorI, TourPlanI } from '../types/types'
import { mapCheckpointsData } from '../utils/mappedFacilityData'

interface FloorPlanRightRailIProps {
  facilityId: string
  currentFloorId: string
  tourPlans: TourPlanI[]
  floors: FloorI[]
  isPanelExpanded: boolean
  onToggleExpansion: () => void
  onReorderFloors?: (floorIds: string[]) => void
}

/**
 * Dispatches the floor-plan right rail to the panel the open drawer asks for —
 * the same shape as FacilitiesTableModal, which switches on its modal type and
 * returns one child per case.
 *
 * Panel-only concerns live here rather than in FloorPlan: the device-highlight
 * handlers and the checkpoint mapping are read straight off the FloorPlan
 * context, so the page keeps the chart and this keeps the rail.
 */
export const FloorPlanRightRail = ({
  facilityId,
  currentFloorId,
  tourPlans,
  floors,
  isPanelExpanded,
  onToggleExpansion,
  onReorderFloors,
}: FloorPlanRightRailIProps) => {
  const drawerOpen = useAppSelector(selectFacilityDrawerOpen)
  const isFacilitiesRebrandEnabled = useFeatureFlag(
    FeatureFlag.facilitiesRebrand
  )
  const {
    checkpointDevices,
    devices,
    onDeviceClicked,
    onTileDragStart,
    setHighlightedDeviceIds,
    setTourStopDeviceIds,
  } = useFloorPlan()

  const mappedCheckpoints = useMemo(
    () => mapCheckpointsData(checkpointDevices),
    [checkpointDevices]
  )

  const onPanelItemHoverIn = (deviceId: string) => {
    setHighlightedDeviceIds([deviceId])
  }

  const onPanelItemsHoverIn = (deviceIds: string[]) => {
    setHighlightedDeviceIds(deviceIds)
    if (isFacilitiesRebrandEnabled) setTourStopDeviceIds(deviceIds)
  }

  const onPanelItemHoverOut = () => {
    setHighlightedDeviceIds(null)
    if (isFacilitiesRebrandEnabled) setTourStopDeviceIds(null)
  }

  // A rebuilt panel is the sole child of the rail: it brings its own collapse
  // chevron and its own full-width SidePanel, so the legacy toggle and wrapper
  // below would otherwise sit beside it as a second flex item and halve its
  // width.
  if (isFacilitiesRebrandEnabled) {
    if (drawerOpen === 'floors') {
      return (
        <DarkFloorsPanel
          currentFloorId={currentFloorId}
          facilityId={facilityId}
          floors={floors}
          isExpanded={isPanelExpanded}
          onReorderFloors={onReorderFloors}
          onToggleExpansion={onToggleExpansion}
        />
      )
    }

    if (drawerOpen === 'devices') {
      return (
        <DarkDevicesPanel
          devices={devices}
          isExpanded={isPanelExpanded}
          onDeviceClicked={onDeviceClicked}
          onDeviceItemHoverIn={onPanelItemHoverIn}
          onDeviceItemHoverOut={onPanelItemHoverOut}
          onTileDragStart={onTileDragStart}
          onToggleExpansion={onToggleExpansion}
        />
      )
    }

    if (drawerOpen === 'checkpoints') {
      return (
        <DarkCheckPointsPanel
          checkpoints={mappedCheckpoints}
          facilityId={facilityId}
          floorId={currentFloorId}
          isExpanded={isPanelExpanded}
          onDeviceItemHoverIn={onPanelItemHoverIn}
          onDeviceItemHoverOut={onPanelItemHoverOut}
          onTileDragStart={onTileDragStart}
          onToggleExpansion={onToggleExpansion}
        />
      )
    }

    if (drawerOpen === 'tour-plans') {
      return (
        <DarkTourPlansPanel
          currentFloorId={currentFloorId}
          facilityId={facilityId}
          isExpanded={isPanelExpanded}
          onDeviceItemHoverIn={onPanelItemHoverIn}
          onDeviceItemHoverOut={onPanelItemHoverOut}
          onDeviceItemsHoverIn={onPanelItemsHoverIn}
          onToggleExpansion={onToggleExpansion}
          tourPlans={tourPlans}
        />
      )
    }
  }

  return (
    <>
      <IconButton
        aria-label={isPanelExpanded ? 'Collapse panel' : 'Expand panel'}
        backgroundColor='white'
        border='1px solid'
        borderColor='gray.200'
        borderRadius='full'
        boxShadow='sm'
        data-testId='FloorPlan:ExpandPanelToggle'
        icon={
          isPanelExpanded ? (
            <ChevronRightIcon boxSize='25px' />
          ) : (
            <ChevronLeftIcon boxSize='25px' />
          )
        }
        onClick={onToggleExpansion}
        position='absolute'
        size='sm'
        top={45}
        transform='translateY(-50%) translateX(-50%)'
        zIndex='1'
      />

      {isPanelExpanded && (
        <Box overflow='hidden' width='100%'>
          {drawerOpen === 'devices' && (
            <DevicesPanel
              devices={devices}
              onDeviceClicked={onDeviceClicked}
              onDeviceItemHoverIn={onPanelItemHoverIn}
              onDeviceItemHoverOut={onPanelItemHoverOut}
              onTileDragStart={onTileDragStart}
            />
          )}
          {drawerOpen === 'floors' && (
            <FloorsPanel
              currentFloorId={currentFloorId}
              facilityId={facilityId}
              floors={floors}
              onReorderFloors={onReorderFloors}
            />
          )}
          {drawerOpen === 'tour-plans' && (
            <TourPlansPanel
              currentFloorId={currentFloorId}
              facilityId={facilityId}
              onDeviceItemHoverIn={onPanelItemHoverIn}
              onDeviceItemHoverOut={onPanelItemHoverOut}
              onDeviceItemsHoverIn={onPanelItemsHoverIn}
              tourPlans={tourPlans}
            />
          )}
          {drawerOpen === 'checkpoints' && (
            <CheckPointsPanel
              checkpoints={mappedCheckpoints}
              facilityId={facilityId}
              floorId={currentFloorId}
              onDeviceItemHoverIn={onPanelItemHoverIn}
              onDeviceItemHoverOut={onPanelItemHoverOut}
              onTileDragStart={onTileDragStart}
            />
          )}
        </Box>
      )}
    </>
  )
}
